import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '../../styles/MatchingStudents.css';
import JobDetailsCard from './MatchingStudents/JobDetailsCard';
import FiltersSection from './MatchingStudents/FiltersSection';
import StudentsTable from './MatchingStudents/StudentsTable';
import PaginationComponent from './MatchingStudents/PaginationComponent';
import Swal from 'sweetalert2';


const MatchingStudents = ({ jobReqId, jobDetails }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});

    const defaultFilters = {
        mappingFilter: 'all',
        location: '',
        experience: '',
        gender: '',
        fromPassedYear: '',
        toPassedYear: '',
        courseName: '',
        branches: '',
        studentName: '',
        placementInterest: '',
        matchScore: 0,
    };

    const [tempFilters, setTempFilters] = useState(defaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

    // ✅ `filters` is the source of truth that drives fetching via useEffect
    const [filters, setFilters] = useState(defaultFilters);

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalStudents: 0,
        branchCounts: {},
        studentsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const [counts, setCounts] = useState({
        all: 0,
        mapped: 0,
        notMapped: 0
    });

    // Refs
    const hasFetchedRef = useRef(false);
    const componentMountedRef = useRef(true);
    const fetchTimeoutRef = useRef(null);

    // Memoized job details
    const memoizedJobDetails = useMemo(() => jobDetails, [
        jobDetails?.jobReqId,
        jobDetails?.jobTitle,
        jobDetails?.companyName,
        jobDetails?.jobLocation,
        jobDetails?.skillSet,
        jobDetails?.qualification,
        jobDetails?.passedOutYear,
        jobDetails?.experienceRequired,
        jobDetails?.bond,
        jobDetails?.interviewType,
        jobDetails?.numberOfOpenings,
        jobDetails?.jobDescription,
        jobDetails?.department,
        jobDetails?.interviewDate,
    ]);

    // Initialize temp filters when appliedFilters changes externally
    useEffect(() => {
        setTempFilters(appliedFilters);
    }, [appliedFilters]);

    // ✅ Core fetch function — accepts both filters and page as arguments
    // so it always uses the latest values, not stale closures
    const fetchMatchingStudents = useCallback(async (page = 1, currentFilters = null) => {
        if (!jobReqId || !componentMountedRef.current) return;

        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            // ✅ Use passed-in filters if provided, otherwise use state
            // This guarantees we always fetch with fresh filter values
            const activeFilters = currentFilters || filters;

            const queryParams = new URLSearchParams({
                page,
                limit: pagination.studentsPerPage,
                mappingFilter: activeFilters.mappingFilter,
                ...(activeFilters.location && { location: activeFilters.location }),
                ...(activeFilters.experience && { experience: activeFilters.experience }),
                ...(activeFilters.courseName && { courseName: activeFilters.courseName }),
                ...(activeFilters.branches && { branches: activeFilters.branches }),
                ...(activeFilters.fromPassedYear && { fromPassedYear: activeFilters.fromPassedYear }),
                ...(activeFilters.toPassedYear && { toPassedYear: activeFilters.toPassedYear }),
                ...(activeFilters.gender && { gender: activeFilters.gender }),
                ...(activeFilters.studentName && { studentName: activeFilters.studentName }),
                ...(activeFilters.placementInterest && { placementInterest: activeFilters.placementInterest }),
                ...(activeFilters.matchScore > 0 && {matchScore: activeFilters.matchScore}),
            });

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/placements/${jobReqId}/matching-students?${queryParams}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const result = await response.json();

                if (result.success && componentMountedRef.current) {
                    setStudents(result.data.matchingStudents || []);
                    setPagination(prev => ({
                        ...prev,
                        ...result.data.pagination,
                        currentPage: page
                    }));
                    if (result.data.counts) {
                        setCounts(result.data.counts);
                    }
                    hasFetchedRef.current = true;
                }
            } else {
                console.error('Failed to fetch matching students:', response.status);
            }
        } catch (error) {
            console.error('Error fetching matching students:', error);
        } finally {
            if (componentMountedRef.current) {
                setLoading(false);
            }
        }
    }, [jobReqId, filters, pagination.studentsPerPage]);

    // ✅ Single useEffect that reacts to filter changes
    // Runs on mount AND whenever `filters` state actually updates
    useEffect(() => {
        componentMountedRef.current = true;

        if (jobReqId) {
            fetchMatchingStudents(1, filters);
        }

        return () => {
            componentMountedRef.current = false;
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobReqId, filters]); // ✅ `filters` drives the fetch — no stale closure issue

    // ─── Filter Handlers ───────────────────────────────────────────────────────

    const handleTempFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    // ✅ FIX: Just update state. The useEffect above handles fetching
    //         once `filters` state actually updates (no more stale closure)
    const handleApplyFilters = useCallback(() => {
        setFilters(tempFilters);          // triggers useEffect → fetch with fresh filters
        setAppliedFilters(tempFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, [tempFilters]);

    const handleClearFilters = useCallback(() => {
        setTempFilters(defaultFilters);
        setFilters(defaultFilters);       // triggers useEffect → fetch with cleared filters
        setAppliedFilters(defaultFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRemoveFilter = useCallback((key) => {
        const updatedFilters = { ...filters };

        if (key === 'fromPassedYear' || key === 'toPassedYear') {
            updatedFilters.fromPassedYear = '';
            updatedFilters.toPassedYear = '';
        } else if (key === 'mappingFilter') {
            updatedFilters[key] = 'all';
        } else {
            updatedFilters[key] = '';
        }

        setFilters(updatedFilters);       // triggers useEffect → fetch with updated filters
        setAppliedFilters(updatedFilters);
        setTempFilters(updatedFilters);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, [filters]);

    // ─── Page Change ───────────────────────────────────────────────────────────

    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && !loading) {
            fetchMatchingStudents(newPage, filters); // ✅ pass current filters explicitly
        }
    }, [pagination.totalPages, loading, fetchMatchingStudents, filters]);

    // ─── Map / Unmap Handlers ──────────────────────────────────────────────────

    const handleMapStudent = useCallback(async (studentId) => {
        if (!studentId || !jobReqId || !jobDetails) return;

        setActionLoading(prev => ({ ...prev, [studentId]: true }));

        try {
            const token = localStorage.getItem('token');

            const requestBody = {
                companyId: jobReqId,
                companyName: jobDetails.companyName,
                studentId: studentId,
                currentStatus: 'Assigned',
                interviewDate: jobDetails?.interviewDate ||
                    jobDetails?.scheduledInterviewDate ||
                    jobDetails?.dateOfInterview ||
                    null
            };

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/mapped-students/map`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            const result = await response.json();

            if (response.ok) {
                await fetchMatchingStudents(pagination.currentPage, filters);
            } else {
                console.error('Failed to map student:', result.message);
                alert(result.message || 'Failed to map student');
            }
        } catch (error) {
            console.error('Error mapping student:', error);
            alert('Error mapping student. Please try again.');
        } finally {
            setActionLoading(prev => ({ ...prev, [studentId]: false }));
        }
    }, [jobReqId, jobDetails, pagination.currentPage, fetchMatchingStudents, filters]);

    const handleUnmapStudent = useCallback(async (studentId, mappingId) => {
        if (!mappingId) {
            console.error('Mapping ID is required to unmap student');
            Swal.fire('Error', 'Cannot unmap: Mapping ID not found', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Are you sure you want to remove this student from this job?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setActionLoading(prev => ({ ...prev, [studentId]: true }));

        try {
            const token = localStorage.getItem('token');

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/mapped-students/${mappingId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const data = await response.json();

            if (response.ok) {
                await Swal.fire('Removed!', 'Student unmapped successfully.', 'success');
                await fetchMatchingStudents(pagination.currentPage, filters);
            } else {
                Swal.fire('Failed', data.message || 'Failed to unmap student', 'error');
            }
        } catch (error) {
            console.error('Error unmapping student:', error);
            Swal.fire('Error', 'Error unmapping student. Please try again.', 'error');
        } finally {
            setActionLoading(prev => ({ ...prev, [studentId]: false }));
        }
    }, [pagination.currentPage, fetchMatchingStudents, filters]);

    // ─── Loading Skeleton ──────────────────────────────────────────────────────

    const LoadingSkeleton = () => (
        <div className="loading-skeleton">
            <div className="skeleton-job-details">
                <div className="skeleton-title"></div>
                <div className="skeleton-grid">
                    {[...Array(12)].map((_, i) => (
                        <div key={i} className="skeleton-detail"></div>
                    ))}
                </div>
            </div>
            <div className="skeleton-filters">
                <div className="skeleton-title"></div>
                <div className="skeleton-filter-grid">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="skeleton-filter"></div>
                    ))}
                </div>
            </div>
            <div className="skeleton-table">
                <div className="skeleton-table-header"></div>
                <div className="skeleton-table-rows">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="skeleton-table-row"></div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (loading && !hasFetchedRef.current) {
        return <LoadingSkeleton />;
    }

    const isAnyFilterApplied = Object.entries(appliedFilters).some(([key, value]) => {
        if (key === 'mappingFilter') return value !== 'all';
        return value !== '';
    });

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="matching-students-container">
            <JobDetailsCard jobDetails={memoizedJobDetails} />

            <FiltersSection
                tempFilters={tempFilters}
                appliedFilters={appliedFilters}
                counts={counts}
                loading={loading}
                department={jobDetails?.department}
                onTempFilterChange={handleTempFilterChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                onRemoveFilter={handleRemoveFilter}
            />

            <StudentsTable
                students={students}
                loading={loading}
                actionLoading={actionLoading}
                tableLoading={loading && hasFetchedRef.current}
                hasFetchedRef={hasFetchedRef}
                totalStudents={pagination.totalStudents}
                branchCounts={pagination.branchCounts}
                currentPage={pagination.currentPage}
                isAnyFilterApplied={isAnyFilterApplied}
                onRefresh={() => fetchMatchingStudents(pagination.currentPage, filters)}
                onClearFilters={handleClearFilters}
                onMapStudent={handleMapStudent}
                onUnmapStudent={handleUnmapStudent}
            />

            {pagination.totalPages > 1 && (
                <PaginationComponent
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    hasPreviousPage={pagination.hasPreviousPage}
                    hasNextPage={pagination.hasNextPage}
                    loading={loading}
                    onPageChange={handlePageChange}
                />
            )}
        </div>
    );
};

export default MatchingStudents;